import { useMemo, useState } from "react";
import { CaretUpIcon, CaretDownIcon } from "@phosphor-icons/react";

/**
 * useSort — one-liner hook to sort rows by any key with asc/desc toggle.
 *
 * const { sorted, sortKey, sortDir, toggleSort } = useSort(rows, "name", "asc");
 *
 * `keyFn` may be a string key or a function (row) => value.
 * Default direction is ascending (per product spec).
 */
export function useSort(rows, defaultKey = "name", defaultDir = "asc") {
  const [sortKey, setSortKey] = useState(defaultKey);
  const [sortDir, setSortDir] = useState(defaultDir); // "asc" | "desc"

  const toggleSort = (key) => {
    if (key === sortKey) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc"); // new column resets to ascending — matches product spec
    }
  };

  const sorted = useMemo(() => {
    const arr = [...(rows || [])];
    arr.sort((a, b) => {
      const va = getVal(a, sortKey);
      const vb = getVal(b, sortKey);
      const na = normalize(va);
      const nb = normalize(vb);
      let cmp;
      if (typeof na === "number" && typeof nb === "number") cmp = na - nb;
      else cmp = String(na).localeCompare(String(nb), "en", { numeric: true, sensitivity: "base" });
      return sortDir === "asc" ? cmp : -cmp;
    });
    return arr;
  }, [rows, sortKey, sortDir]);

  return { sorted, sortKey, sortDir, toggleSort };
}

function getVal(row, key) {
  if (typeof key === "function") return key(row);
  if (!row) return "";
  const parts = String(key).split(".");
  let v = row;
  for (const p of parts) { if (v == null) return ""; v = v[p]; }
  return v;
}
function normalize(v) {
  if (v == null) return "";
  if (typeof v === "number") return v;
  if (typeof v === "string" && v.match(/^-?\d+(\.\d+)?$/)) return parseFloat(v);
  return v;
}

/**
 * SortHeader — clickable header cell showing an asc/desc caret next to the label.
 */
export function SortHeader({ label, sortKey, active, dir, onClick, align = "left", className = "", testid }) {
  const alignClass = align === "right" ? "text-right justify-end" : align === "center" ? "text-center justify-center" : "text-left";
  return (
    <button
      type="button"
      onClick={() => onClick(sortKey)}
      className={`w-full flex items-center gap-1 ${alignClass} uppercase text-xs tracking-wider hover:text-brand-orange-ink transition-colors ${active ? "text-brand-blue" : "text-slate-500"} ${className}`}
      data-testid={testid || `sort-${sortKey}`}
    >
      <span>{label}</span>
      {active ? (
        dir === "asc" ? <CaretUpIcon size={10} weight="bold" /> : <CaretDownIcon size={10} weight="bold" />
      ) : (
        <span className="opacity-30" style={{ fontSize: 8 }}>↕</span>
      )}
    </button>
  );
}

/**
 * Product-list summary string — turns [{name:"Domestic 14.2kg", filled:5, empty:3}, ...]
 * into "14.2kg · 5F / 3E".
 */
export function productSummary(products) {
  if (!products || products.length === 0) return "—";
  return products.map((p) => {
    const short = shortName(p.name || "");
    return `${short} · ${p.filled || 0}F / ${p.empty || 0}E`;
  }).join(" · ");
}

// Strips "Domestic ", "Commercial ", " kg" etc for compact display in table rows.
export function shortName(name) {
  if (!name) return "—";
  const s = String(name)
    .replace(/^(domestic|commercial|mini)\s+/i, "")
    .replace(/\s*cylinder\s*$/i, "")
    .replace(/\s*\bkg\b/i, "kg")
    .trim();
  return s || name;
}

/** Signed variance badge: green (+), red (-), neutral (0). */
export function VarianceCell({ value, testid }) {
  const v = Number(value) || 0;
  const cls = v > 0 ? "text-[#B45309]" : v < 0 ? "text-[#166534]" : "text-slate-400";
  const sign = v > 0 ? "+" : "";
  return (
    <span className={`font-mono-num font-semibold ${cls}`} data-testid={testid}>
      {sign}{v}
    </span>
  );
}
